import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
    IoSwapHorizontalOutline, IoSearchOutline, IoClose, IoGridOutline,
    IoLocationOutline, IoMapOutline, IoChevronForward, IoStorefrontOutline
} from 'react-icons/io5'
import { isDarkMode } from '../../Auxiliaries/theme'
import { getPlace, getUser, enterUnit, backToPoolOverview } from '../../Auxiliaries/unitNavigation'
import '../../Portal/Portal.css'
import '../../Portal/importar/ImportarDados.css'
import './SideBarUnits.css'

// ─────────────────────────────────────────────────────────────────────────────
//  TROCAR DE UNIDADE — gaveta da equipe BIWAPP (admin/"virginia") e do pool.
//  Mesma interface de antes (ref.open() e o botão), padrão novo:
//   - busca por nome, endereço, cidade (sem acento), já com o foco;
//   - ↑ ↓ Enter para escolher e Esc para fechar;
//   - avatar na cor da rede (5àsec, Dryclean USA, eLav, OMO, outras);
//   - a unidade atual marcada; unidade desativada bloqueada para o pool.
//  O Admin Console já está no menu da conta, então não se repete aqui; o pool
//  ganha "Visão geral" no topo (é por onde ele volta ao painel das unidades).
// ─────────────────────────────────────────────────────────────────────────────

const semAcento = (t) => String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
const SITUACAO = { $: ['pagante', 'Pagante'], trial: ['teste', 'Teste grátis'], inactive: ['desativada', 'Desativada'] }
const situacaoDe = (p) => SITUACAO[p.status] || ['free', 'Free']
// cor por rede/franquia: pelo brand_name da loja e, se faltar, pelo nome
const REDES = [
    ['5asec', '5àsec', '#7c3aed', /5\s*a\s*sec/],
    ['drycleanusa', 'Dryclean USA', '#dc2626', /dry\s*clean/],
    ['elav', 'eLav', '#2563eb', /\belav\b/],
    ['omo', 'OMO', '#1e3a8a', /\bomo\b/]
]
export const redeDe = (p) => {
    const marca = semAcento(p && p.brand_name)
    const nome = semAcento(p && p.name)
    const r = REDES.find(([id, , , re]) => marca === id || re.test(marca) || re.test(nome))
    return r ? { id: r[0], nome: r[1], cor: r[2] } : { id: 'outro', nome: 'Outra rede', cor: '#16a34a' }
}
const iniciais = (nome) => String(nome || '?').replace(/[^\p{L}\p{N} ]/gu, ' ').trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?'

// destaca o trecho buscado no nome
function Destaque({ texto, busca }) {
    const t = String(texto || '')
    const q = semAcento(busca.trim())
    if (!q) return t
    const i = semAcento(t).indexOf(q)
    if (i < 0) return t
    return <>{t.slice(0, i)}<mark>{t.slice(i, i + q.length)}</mark>{t.slice(i + q.length)}</>
}

const SideBarUnits = forwardRef(function SideBarUnits(props, ref) {
    const [aberta, setAberta] = useState(false)
    const [busca, setBusca] = useState('')
    const [sel, setSel] = useState(0)
    const [entrando, setEntrando] = useState(null)
    const [lojas, setLojas] = useState([])
    const refBusca = useRef(null)
    const refLista = useRef(null)

    const isPool = localStorage.getItem('pool_secret') === 'POOL_VL'
    const isEquipe = localStorage.getItem('biwapp_secret') === 'VL101224' || localStorage.getItem('username') === 'virginia'
    const podeTrocar = isEquipe || isPool
    const atual = localStorage.getItem('resd')
    const noPainel = ['admin', 'pool'].includes(localStorage.getItem('viewType'))

    const abrir = () => {
        let lista = []
        try { lista = JSON.parse(localStorage.getItem('places') || '[]') } catch (e) { lista = [] }
        setLojas(Array.isArray(lista) ? lista.filter((p) => p && p.userId) : [])
        setBusca(''); setSel(0); setEntrando(null)
        setAberta(true)
    }
    useImperativeHandle(ref, () => ({ open: abrir }))

    useEffect(() => {
        if (!aberta) return undefined
        const t = setTimeout(() => refBusca.current && refBusca.current.focus(), 60)
        const antes = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => { clearTimeout(t); document.body.style.overflow = antes }
    }, [aberta])

    const contagem = useMemo(() => {
        const c = { todas: lojas.length, pagante: 0, teste: 0, free: 0, desativada: 0 }
        for (const p of lojas) c[situacaoDe(p)[0]]++
        return c
    }, [lojas])

    const filtradas = useMemo(() => {
        const q = semAcento(busca.trim())
        return lojas
            .filter((p) => !q || semAcento([p.name, p.address, p.city_state, p.brand_name].join(' ')).includes(q))
            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR'))
    }, [lojas, busca])


    // ordem de navegação pelo teclado: (pool) Visão geral no topo e a lista
    const itens = useMemo(() => [...(isPool ? [{ especial: true }] : []), ...filtradas.map((p) => ({ p }))], [filtradas, isPool])
    const primeiro = isPool ? 1 : 0
    useEffect(() => { setSel(isPool && itens.length > 1 && busca.trim() ? 1 : 0) }, [busca]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const el = refLista.current && refLista.current.querySelector('[data-i="' + sel + '"]')
        if (el) el.scrollIntoView({ block: 'nearest' })
    }, [sel])

    const bloqueada = (p) => p.status === 'inactive' && !isEquipe
    const escolher = async (item) => {
        if (!item || entrando) return
        if (item.especial) { setEntrando('especial'); return backToPoolOverview() }
        const p = item.p
        if (bloqueada(p)) return
        setEntrando(p.userId)
        const [place, user] = await Promise.all([getPlace(p.userId), getUser(p.userId)])
        if (!place || !user) { setEntrando(null); return }
        enterUnit(place, user)
    }

    const tecla = (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => (s + 1) % itens.length) }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => (s - 1 + itens.length) % itens.length) }
        else if (e.key === 'Enter') { e.preventDefault(); escolher(itens[sel]) }
        else if (e.key === 'Escape') setAberta(false)
    }

    const linha = (item, i) => {
        const p = item.p
        const [cls, rot] = situacaoDe(p)
        const eAtual = String(p.userId) === String(atual) && !noPainel
        const trava = bloqueada(p)
        const cor = redeDe(p)
        return (
            <button key={p.userId} type="button" data-i={i} role="option" aria-selected={sel === i}
                className={'tu-linha' + (sel === i ? ' sel' : '') + (eAtual ? ' atual' : '') + (trava ? ' travada' : '')}
                onMouseEnter={() => setSel(i)} onClick={() => escolher(item)} disabled={trava} title={trava ? 'Unidade desativada' : undefined}>
                <span className="tu-av" style={{ background: cor.cor }} title={cor.nome}>{iniciais(p.name)}</span>
                <span className="tu-txt">
                    <strong><Destaque texto={p.name} busca={busca} /></strong>
                    <small><IoLocationOutline size={12} /> {p.address ? <Destaque texto={p.address} busca={busca} /> : 'Endereço não informado'}</small>
                    {p.city_state && <small className="tu-cidade"><IoMapOutline size={12} /> <Destaque texto={p.city_state} busca={busca} /></small>}
                </span>
                {eAtual ? <span className="tu-selo atual">Você está aqui</span> : <span className={'tu-selo ' + cls}>{rot}</span>}
                {entrando === p.userId ? <span className="imp-giro tu-giro" /> : <IoChevronForward size={16} className="tu-seta" />}
            </button>
        )
    }

    const gaveta = aberta && createPortal(
        <div className={'pt tu-raiz' + (isDarkMode() ? ' dark' : '')} onKeyDown={tecla}>
            <div className="tu-veu" onClick={() => setAberta(false)} />
            <aside className="tu-gaveta" role="dialog" aria-modal="true" aria-label="Trocar de unidade">
                <header className="tu-topo">
                    <span className="tu-topo-ic"><IoSwapHorizontalOutline size={20} /></span>
                    <div>
                        <h2>Trocar de unidade</h2>
                        <p>{contagem.todas} unidade{contagem.todas === 1 ? '' : 's'} · {contagem.pagante} pagante{contagem.pagante === 1 ? '' : 's'}{contagem.teste ? ' · ' + contagem.teste + ' em teste' : ''}</p>
                    </div>
                    <button type="button" className="tu-fechar" onClick={() => setAberta(false)} aria-label="Fechar"><IoClose size={20} /></button>
                </header>
                <div className="tu-busca">
                    <IoSearchOutline size={17} />
                    <input ref={refBusca} value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome, endereço ou cidade…" aria-label="Procurar unidade" autoComplete="off" />
                    {busca && <button type="button" onClick={() => { setBusca(''); refBusca.current && refBusca.current.focus() }} aria-label="Limpar"><IoClose size={15} /></button>}
                </div>
                <div className="tu-lista" ref={refLista} role="listbox">
                    {isPool && (
                        <button type="button" data-i={0} className={'tu-linha tu-especial' + (sel === 0 ? ' sel' : '')} onMouseEnter={() => setSel(0)} onClick={() => escolher(itens[0])}>
                            <span className="tu-av especial"><IoGridOutline size={18} /></span>
                            <span className="tu-txt"><strong>Visão geral</strong><small>Todas as suas unidades num lugar só</small></span>
                            {entrando === 'especial' ? <span className="imp-giro tu-giro" /> : <IoChevronForward size={16} className="tu-seta" />}
                        </button>
                    )}
                    {busca && <div className="tu-grupo"><IoStorefrontOutline size={13} /> {filtradas.length + ' encontrada' + (filtradas.length === 1 ? '' : 's')}</div>}
                    {filtradas.map((p, k) => linha({ p }, primeiro + k))}
                    {filtradas.length === 0 && (
                        <div className="tu-vazio"><IoSearchOutline size={24} /><strong>Nenhuma unidade encontrada</strong><span>Tente outro nome, bairro ou cidade.</span></div>
                    )}
                </div>
                <footer className="tu-rodape"><kbd>↑</kbd><kbd>↓</kbd> escolher · <kbd>Enter</kbd> abrir · <kbd>Esc</kbd> fechar</footer>
            </aside>
        </div>,
        document.querySelector('.App') || document.body
    )

    return (
        <>
            {podeTrocar
                ? <button type="button" onClick={abrir} className="tu-botao"><IoSwapHorizontalOutline size={16} /> {noPainel ? 'Selecione uma unidade' : (localStorage.getItem('place_name') || 'Unidade')}</button>
                : <span className="tu-botao fixo"><IoStorefrontOutline size={16} /> {localStorage.getItem('place_name')}</span>}
            {gaveta}
        </>
    )
})

export default SideBarUnits
